import { imageBaseURL } from "@/lib/utils"
import type { ProductImage } from "./products"
import type { TreatmentId } from "./treatments"

/**
 * Capa editorial de los tratamientos: contenido de marketing/informativo que NO
 * vive en `treatments.ts` (ese archivo modela precios, paquetes y productos).
 *
 * El texto está redactado a partir del contenido publicado en biosany.com para
 * cada tratamiento. Para editar la ficha de un padecimiento, cambia solo su
 * entrada aquí. Cada tratamiento tiene la MISMA forma (contrato `TreatmentContent`).
 */

export interface TreatmentFaq {
	q: string
	a: string
}

export interface TreatmentContent {
	/** Gancho corto que acompaña al título en el encabezado. */
	tagline: string
	/** Introducción: qué es y para qué sirve el tratamiento. */
	description: string
	/** Síntomas / problemas que resuelve (se muestran como chips). */
	solves: string[]
	/** Cómo actúa la bioestimulación celular en este padecimiento. */
	how: string
	/** Foto para la sección "cómo funciona". */
	image: ProductImage
	/** Preguntas frecuentes específicas del tratamiento. */
	faqs: TreatmentFaq[]
}

/** FAQ común: la diferencia entre paquetes. Es factual e idéntica en todos. */
export const planFaq: TreatmentFaq = {
	q: "¿Qué diferencia hay entre el paquete básico y el completo?",
	a: "El básico está pensado para 2 semanas: ideal para empezar o para casos leves. El completo dura 1 mes, incluye mayor cantidad de producto y es el recomendado para resultados más profundos y duraderos."
}

/** FAQ común: seguridad. La bioestimulación no es un fármaco. */
export const safeFaq: TreatmentFaq = {
	q: "¿Tiene efectos secundarios? ¿Puedo tomarlo con otros medicamentos?",
	a: "No es un fármaco ni un producto naturista: trabaja en armonía con tu organismo, sin efectos secundarios. Puede usarse a cualquier edad y junto a otros tratamientos. Ante cualquier duda, consulta a tu médico."
}

const img = (file: string): string => imageBaseURL(`og/treatments/${file}`)

const TREATMENT_CONTENT: Record<TreatmentId, TreatmentContent> = {
	acidez: {
		tagline: "Calma la acidez, la gastritis y el reflujo desde su origen.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para tratar los síntomas de la acidez crónica y ocasional, así como la gastritis y el reflujo. Actúa en la raíz del problema, no solo en el síntoma.",
		solves: [
			"Ardor y pirosis",
			"Regurgitación ácida",
			"Dificultad para tragar",
			"Dolor de pecho",
			"Malestar estomacal",
			"Falta de sueño"
		],
		image: {
			source: img("acidez.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para la acidez estomacal"
		},
		how: "Estimula las funciones celulares de tu sistema digestivo, equilibra el pH de tu cuerpo y fortalece las defensas naturales de tu organismo, reduciendo la producción excesiva de ácido y protegiendo la mucosa gástrica.",
		faqs: [
			planFaq,
			{
				q: "¿En cuánto tiempo sentiré alivio?",
				a: "La mayoría percibe menos ardor y mejor digestión durante las primeras dos semanas de uso constante; el paquete completo de un mes consolida el resultado."
			},
			safeFaq
		]
	},
	acne: {
		tagline: "Piel más limpia y sana, tratando el acné desde dentro y por fuera.",
		description:
			"Este tratamiento de bioestimulación celular previene y combate los problemas en la piel causados por el acné —granos, espinillas, puntos negros, manchas e irritación—, con efectividad visible desde la primera semana en todo tipo de piel.",
		solves: [
			"Granos y espinillas",
			"Piel grasa y brillosa",
			"Enrojecimiento e irritación",
			"Dolor y sensibilidad",
			"Pústulas y nódulos",
			"Cicatrices"
		],
		image: {
			source: img("acne.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para el acné"
		},
		how: "Los productos externos (DermoGel y Jabón Neutro) estimulan la piel y forman una barrera contra las bacterias, mientras que los internos (Shalósh Bayóm y Agua Vita) limpian toxinas y regulan el pH para equilibrar las hormonas que originan el acné.",
		faqs: [
			planFaq,
			{
				q: "¿Sirve para cualquier tipo de piel y edad?",
				a: "Sí. Está formulado para actuar a nivel celular reparando la piel dañada, y es apto para todas las edades y tipos de piel."
			},
			safeFaq
		]
	},
	ansiedad: {
		tagline: "Recupera la calma: menos nerviosismo, mejor sueño y concentración.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para tratar los síntomas de la ansiedad, el nerviosismo y el estrés, actuando sobre el sistema nervioso central para restablecer el equilibrio de forma natural.",
		solves: ["Nerviosismo", "Insomnio", "Estrés y tensión muscular", "Problemas de concentración", "Falta de descanso"],
		image: {
			source: img("ansiedad.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para la ansiedad"
		},
		how: "Actúa directamente en la raíz del problema, en el sistema nervioso central, restableciendo el equilibrio de las funciones cerebrales de una forma natural y ayudando a regular el descanso y la memoria.",
		faqs: [
			planFaq,
			{
				q: "¿Es adictivo o me dará somnolencia durante el día?",
				a: "No. No es un fármaco ni un sedante: ayuda a tu organismo a recuperar su equilibrio natural, sin dependencia ni somnolencia diurna."
			},
			safeFaq
		]
	},
	artritis: {
		tagline: "Menos dolor e inflamación en tus articulaciones, día con día.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para tratar los principales síntomas de la artritis: la inflamación y el dolor en las articulaciones, regenerando los tejidos dañados de forma progresiva.",
		solves: ["Dolor articular", "Inflamación e hinchazón", "Rigidez", "Movilidad reducida"],
		image: {
			source: img("artritis.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para la artritis"
		},
		how: "Fortalece las defensas naturales del cuerpo y restablece las funciones celulares, regenerando los tejidos dañados de cartílagos y huesos mientras reduce progresivamente la inflamación y el dolor.",
		faqs: [
			planFaq,
			{
				q: "¿Cómo se aplica en las zonas con dolor?",
				a: "Además de las tomas de Shalósh Bayóm y Agua Vita, se aplica DermoGel directamente sobre las articulaciones con dolor e inflamación para un alivio localizado."
			},
			safeFaq
		]
	},
	amenorrea: {
		tagline: "Restablece tu ciclo menstrual regulando tus hormonas de forma natural.",
		description:
			"Este tratamiento de bioestimulación celular trata la ausencia de menstruación actuando directamente sobre el sistema reproductor femenino, restableciendo los tejidos uterinos y las funciones hormonales sin químicos ni fármacos agresivos.",
		solves: [
			"Ausencia de menstruación",
			"Desajustes hormonales",
			"Infertilidad asociada",
			"Dolor de cabeza",
			"Acné hormonal"
		],
		image: {
			source: img("amenorrea.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para la amenorrea"
		},
		how: "Regula las funciones hormonales y permite que el organismo absorba los nutrientes que necesita, restableciendo el ciclo menstrual de manera natural y resolviendo los problemas hormonales asociados.",
		faqs: [
			planFaq,
			{
				q: "¿Cuánto dura la aplicación completa?",
				a: "El paquete completo acompaña un ciclo de aproximadamente 30 días, combinando tomas y aplicaciones para restablecer la función hormonal de forma gradual."
			},
			safeFaq
		]
	},
	asma: {
		tagline: "Respira mejor: regula y disminuye los síntomas del asma.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para regular y disminuir los síntomas del asma y otros problemas respiratorios. Aunque el asma es crónica, permite lograr cambios significativos en tu calidad de vida.",
		solves: ["Falta de aire", "Tos crónica", "Opresión en el pecho", "Fatiga al respirar"],
		image: {
			source: img("asma.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para el asma"
		},
		how: "Restaura y reduce la inflamación y el estrechamiento de los conductos bronquiales, facilitando la respiración y aumentando la capacidad pulmonar, a la vez que fortalece las defensas naturales del organismo.",
		faqs: [
			planFaq,
			{
				q: "¿Sustituye a mi inhalador?",
				a: "No lo sustituye: es un complemento natural que ayuda a reducir la frecuencia e intensidad de los síntomas. Mantén tu medicación de rescate y consulta a tu médico."
			},
			safeFaq
		]
	},
	bronquitis: {
		tagline: "Despeja tus vías respiratorias y calma la tos desde los primeros días.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para tratar los síntomas de la bronquitis aguda y crónica, la tos y la congestión en el pecho, con resultados visibles desde los primeros tres días.",
		solves: ["Congestión en el pecho", "Tos e irritación de garganta", "Dificultad para respirar"],
		image: {
			source: img("bronquitis.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para la bronquitis"
		},
		how: "Fortalece las defensas naturales del cuerpo, despeja las vías respiratorias, reduce la inflamación de los bronquios y mejora la capacidad pulmonar y la oxigenación de la sangre.",
		faqs: [
			planFaq,
			{
				q: "¿En cuántos días notaré mejoría?",
				a: "Muchas personas notan mejoría desde los primeros tres días; el paquete completo de un mes ayuda a resolver los cuadros más persistentes."
			},
			safeFaq
		]
	},
	cancer: {
		tagline: "Acompaña tu tratamiento fortaleciendo tus defensas y tu energía.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para tratar los síntomas del cáncer y acompañar los procesos de quimioterapia y radioterapia, mejorando las funciones celulares y estimulando la producción de glóbulos blancos.",
		solves: ["Fatiga crónica", "Pérdida de apetito", "Problemas digestivos", "Malestar general"],
		image: {
			source: img("cancer.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para el cáncer"
		},
		how: "Estimula la producción de glóbulos blancos, aumenta la oxigenación celular y facilita la eliminación de toxinas, fortaleciendo las defensas del organismo frente a los agentes que propician la enfermedad.",
		faqs: [
			planFaq,
			{
				q: "¿Puedo usarlo durante la quimioterapia o radioterapia?",
				a: "Sí, está pensado para acompañar esos procesos y ayudar a sobrellevar sus efectos. Coméntalo siempre con tu oncólogo antes de iniciarlo."
			},
			safeFaq
		]
	},
	cataratas: {
		tagline: "Recupera claridad de visión sin procedimientos invasivos.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para tratar los síntomas de las cataratas, la visión borrosa y la pérdida de visión, estimulando directamente las funciones celulares del ojo.",
		solves: ["Visión borrosa", "Pérdida de visión", "Deslumbramiento", "Sensibilidad a la luz"],
		image: {
			source: img("cataratas.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para las cataratas"
		},
		how: "Estimula las funciones celulares del cristalino para reducir la opacidad y mejorar gradualmente la visión, sin necesidad de procedimientos quirúrgicos invasivos.",
		faqs: [
			planFaq,
			{
				q: "¿Cómo se aplican las gotas?",
				a: "Se aplican gotas de Taftéfet en cada ojo por la noche, acompañadas de lavados con Agua Vita y tomas de Shalósh Bayóm. El paquete incluye las cantidades para cada duración."
			},
			safeFaq
		]
	},
	conjuntivitis: {
		tagline: "Ojos limpios y sin irritación desde los primeros días.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para tratar los síntomas de la conjuntivitis, los ojos rojos y la picazón, purificando y desintoxicando el ojo de forma natural.",
		solves: ["Ojos rojos", "Picazón ocular", "Secreción y lagrimeo", "Inflamación e irritación"],
		image: {
			source: img("conjuntivitis.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para la conjuntivitis"
		},
		how: "Rehabilita las funciones celulares oculares con Taftéfet, que purifica y desintoxica el ojo, reduciendo la inflamación, la irritación y las secreciones, con resultados desde los primeros tres días.",
		faqs: [
			planFaq,
			{
				q: "¿Sirve para conjuntivitis viral y bacteriana?",
				a: "Ayuda a aliviar la irritación, el enrojecimiento y las secreciones en ambos casos. Si hay fiebre o dolor intenso, acude también con tu médico."
			},
			safeFaq
		]
	},
	dermatitis: {
		tagline: "Repara la piel seca, escamosa e irritada desde el nivel celular.",
		description:
			"Este tratamiento de bioestimulación celular previene y combate los problemas en la piel causados por la dermatitis —piel seca, con escamas, enrojecida e irritada—, con resultados visibles desde los primeros dos días.",
		solves: ["Piel seca e inflamada", "Escamas y ronchas", "Enrojecimiento e irritación", "Comezón intensa"],
		image: {
			source: img("dermatitis.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para la dermatitis"
		},
		how: "Actúa de dos formas: por fuera, DermoGel y Jabón Neutro forman una barrera contra bacterias y agentes irritantes; por dentro, Shalósh Bayóm y Agua Vita limpian toxinas, regulan el pH y fortalecen el sistema inmunológico.",
		faqs: [
			planFaq,
			{
				q: "¿Es apto para pieles sensibles y niños?",
				a: "Sí. Su fórmula es natural e hipoalergénica y está pensada para actuar reparando la piel dañada, apta para pieles sensibles y para todas las edades."
			},
			safeFaq
		]
	},
	diabetes: {
		tagline: "Ayuda a controlar tu azúcar estimulando tu páncreas de forma natural.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para tratar los síntomas de la diabetes tipo 1, ayudar a controlar el azúcar en la sangre y prevenir complicaciones, enfocándose en las células beta del páncreas.",
		solves: [
			"Azúcar alta en sangre",
			"Fatiga y debilidad",
			"Cicatrización lenta",
			"Sed y micción frecuente",
			"Visión borrosa"
		],
		image: {
			source: img("diabetes.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para la diabetes"
		},
		how: "Actúa directamente en la raíz del problema, estimulando las funciones de las células beta del páncreas para mejorar la producción de insulina y la absorción de glucosa en las células.",
		faqs: [
			planFaq,
			{
				q: "¿Puedo seguir aplicándome insulina?",
				a: "Sí. El tratamiento es un complemento natural que no interrumpe tu esquema de insulina. Sigue midiendo tu glucosa y consulta a tu médico ante cualquier ajuste."
			},
			safeFaq
		]
	},
	"diabetes-tipo-2": {
		tagline: "Controla tu azúcar y mejora tu calidad de vida, día con día.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para tratar los síntomas de la diabetes tipo 2, controlar el azúcar en la sangre y mejorar la calidad de vida, estimulando las células beta del páncreas.",
		solves: [
			"Azúcar alta en sangre",
			"Problemas de circulación",
			"Heridas y úlceras que no sanan",
			"Sed y micción frecuente",
			"Hambre frecuente",
			"Problemas de visión"
		],
		image: {
			source: img("diabetes-tipo-2.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para la diabetes tipo 2"
		},
		how: "Actúa directamente en la raíz del problema, estimulando las funciones de las células beta del páncreas para regularizar de forma natural los niveles de glucosa, sin interrumpir tu tratamiento con insulina.",
		faqs: [
			planFaq,
			{
				q: "¿Ayuda con las heridas que no cicatrizan?",
				a: "Sí. Para lesiones y úlceras se limpia la zona, se aplica DermoGel en abundancia y se rocía con Lazihúm, favoreciendo la regeneración de la piel."
			},
			safeFaq
		]
	},
	"envejecimiento-prematuro": {
		tagline: "Recupera vitalidad y firmeza: envejece mejor, no más rápido.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para combatir los síntomas del envejecimiento prematuro —arrugas, flacidez, cansancio y pérdida de peso—, estimulando las funciones celulares del cuerpo y la piel.",
		solves: ["Arrugas y flacidez", "Piel manchada", "Cansancio y falta de energía", "Pérdida de peso y masa muscular"],
		image: {
			source: img("envejecimiento-prematuro.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para el envejecimiento prematuro"
		},
		how: "Estimula las funciones celulares para que las células trabajen de forma más eficiente gastando menos energía, acelerando la regeneración natural del cuerpo sin procedimientos invasivos ni costosos.",
		faqs: [
			planFaq,
			{
				q: "¿A partir de qué edad conviene usarlo?",
				a: "Puede usarse en cualquier etapa adulta. Cuanto antes se incorpora a la rutina, mejor acompaña la regeneración celular y previene el desgaste."
			},
			safeFaq
		]
	},
	estrenimiento: {
		tagline: "Regulariza tu digestión y evacúa sin dolor, de forma natural.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para tratar los síntomas del estreñimiento crónico y ocasional, restaurando las funciones de las células intestinales sin fármacos agresivos.",
		solves: ["Estreñimiento crónico u ocasional", "Hinchazón abdominal", "Gases acumulados", "Malestar general"],
		image: {
			source: img("estrenimiento.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para el estreñimiento"
		},
		how: "Actúa directamente en el sistema digestivo restableciendo las funciones de las células intestinales, regulando el peristaltismo para promover una evacuación regular y sin dolor, y restaurando el equilibrio metabólico.",
		faqs: [
			planFaq,
			{
				q: "¿Genera dependencia como los laxantes?",
				a: "No. En lugar de forzar la evacuación, ayuda a tu intestino a recuperar su ritmo natural, sin dependencia ni irritación."
			},
			safeFaq
		]
	},
	heridas: {
		tagline: "Cicatriza más rápido y previene infecciones, en todo tipo de heridas.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para tratar todo tipo de heridas en la piel —desde raspones y cortadas hasta quemaduras y úlceras—, con una fórmula 100% natural e hipoalergénica apta para cualquier edad.",
		solves: ["Raspaduras y cortes", "Ampollas", "Quemaduras", "Mordeduras", "Úlceras"],
		image: {
			source: img("heridas.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para las heridas"
		},
		how: "Actúa directamente en la piel ayudando a reconstruir y regenerar las células más rápido, previniendo infecciones y reduciendo la inflamación gracias a la bioestimulación celular.",
		faqs: [
			planFaq,
			{
				q: "¿Sirve para quemaduras y heridas profundas?",
				a: "Sí. Para heridas leves basta con lavar y aplicar DermoGel; para heridas profundas se limpia la zona, se aplica Lazihúm y se cubre con gasa humedecida en DermoGel, cambiándola a diario."
			},
			safeFaq
		]
	},
	menopausia: {
		tagline: "Sobrelleva la menopausia con más energía, calma y bienestar.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para tratar algunos de los síntomas más comunes de la menopausia, como la sequedad vaginal, la ansiedad, la falta de sueño, la fatiga y la disminución de la masa muscular.",
		solves: [
			"Sequedad vaginal",
			"Insomnio, ansiedad y estrés",
			"Desequilibrio hormonal",
			"Pérdida de masa muscular",
			"Estrés oxidativo"
		],
		image: {
			source: img("menopausia.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para la menopausia"
		},
		how: "Estimula las células y mejora su capacidad natural para sanar y regenerar. Los productos desintoxican, regulan el pH del cuerpo y favorecen la producción hormonal para aliviar los síntomas.",
		faqs: [
			planFaq,
			{
				q: "¿Incluye un plan de alimentación?",
				a: "El acompañamiento contempla recomendaciones de alimentación para potenciar los resultados; el paquete completo es el más indicado para un mes de seguimiento."
			},
			safeFaq
		]
	},
	parkinson: {
		tagline: "Apoya tu sistema nervioso: menos temblor y más estabilidad.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para tratar los síntomas del Parkinson y otros problemas neurológicos, como el Alzheimer, la esclerosis múltiple y la epilepsia.",
		solves: [
			"Temblor en manos, piernas y mandíbula",
			"Rigidez muscular",
			"Insomnio",
			"Dificultad para deglutir",
			"Problemas de movilidad"
		],
		image: {
			source: img("parkinson.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para el Parkinson"
		},
		how: "La bioestimulación celular mejora la comunicación entre las células, permitiendo una mejor transmisión de los impulsos nerviosos, regulando el sistema nervioso, mejorando la circulación y fortaleciendo el sistema inmunológico.",
		faqs: [
			planFaq,
			{
				q: "¿En qué consiste la rutina diaria?",
				a: "Combina tomas de Shalósh Bayóm y té con Agua Vita, aplicación de DermoGel con masaje, gotas oculares y ejercicios de movilidad. El paquete detalla cada paso."
			},
			safeFaq
		]
	},
	prostatitis: {
		tagline: "Alivia la próstata: menos dolor, mejor orina y bienestar íntimo.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para tratar los síntomas de la prostatitis y otros problemas de la próstata, como la hiperplasia benigna (BPH) y la disfunción eréctil.",
		solves: ["Dolor prostático", "Dificultad para orinar", "Hiperplasia benigna (BPH)", "Disfunción eréctil"],
		image: {
			source: img("prostatitis.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para la prostatitis"
		},
		how: "La bioestimulación celular mejora la circulación sanguínea de la próstata y su oxigenación, estimulando al cuerpo a sanarse a sí mismo, regenerando células y fortaleciendo sus defensas naturales.",
		faqs: [
			planFaq,
			{
				q: "¿Incluye ejercicios además de los productos?",
				a: "Sí. Se recomienda un ejercicio de punta-talón diario que complementa las tomas de Shalósh Bayóm, Agua Vita y la aplicación de DermoGel."
			},
			safeFaq
		]
	},
	sobrepeso: {
		tagline: "Baja de peso de forma natural, con resultados desde la primera semana.",
		description:
			"Este tratamiento es una solución efectiva, natural y sin efectos secundarios para combatir el sobrepeso, diseñado para brindar resultados visibles desde la primera semana mediante bioestimulación celular.",
		solves: [
			"Retención de toxinas",
			"Metabolismo lento",
			"Digestión deficiente",
			"Antojos y falta de control",
			"Baja energía"
		],
		image: {
			source: img("sobrepeso.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para el sobrepeso"
		},
		how: "Con la ingesta diaria de Agua Vita y Shalósh Bayóm, aprovecha las propiedades naturales del organismo para quemar grasa de forma efectiva, nutrir el cuerpo y alcanzar el equilibrio necesario para reducir el sobrepeso.",
		faqs: [
			planFaq,
			{
				q: "¿Necesito hacer dieta?",
				a: "Sí. El tratamiento incluye una dieta durante los primeros días de desintoxicación y recomienda 30 minutos diarios de caminata o ejercicio para potenciar los resultados."
			},
			safeFaq
		]
	},
	vph: {
		tagline: "Refuerza tus defensas frente al VPH y otras infecciones.",
		description:
			"Este tratamiento de bioestimulación celular está diseñado para tratar los síntomas del virus del papiloma humano, las verrugas genitales, el herpes y otras enfermedades de transmisión sexual.",
		solves: ["Verrugas genitales", "Dolor e inflamación", "Herpes", "Infecciones de transmisión sexual"],
		image: {
			source: img("vph.jpg"),
			width: 1200,
			height: 630,
			alt: "Tratamiento de bioestimulación celular para el virus del papiloma humano"
		},
		how: "La bioestimulación celular mejora la circulación sanguínea y la oxigenación celular, lo que permite una mejor respuesta del sistema inmunológico y una mayor capacidad para combatir las infecciones.",
		faqs: [
			planFaq,
			{
				q: "¿La aplicación es distinta para hombres y mujeres?",
				a: "Sí. Todos hacen gárgaras con Shalósh Bayóm y beben Agua Vita; las mujeres aplican productos por vía vaginal durante la noche y los hombres aplican tratamientos tópicos tras el baño y por las noches."
			},
			safeFaq
		]
	}
}

export const getTreatmentContent = (id: TreatmentId): TreatmentContent => TREATMENT_CONTENT[id]

export default TREATMENT_CONTENT
